import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/index';
import { selectUser, selectIsAuthenticated } from '@features/auth/authSlice';
import AccessDenied from './AccessDenied';

interface PermissionRouteProps {
  requiredRoles?: string[];
  requiredPermissions?: string[];
  requireAdmin?: boolean;
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  requiredRoles = [],
  requiredPermissions = [],
  requireAdmin = false,
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const location = useLocation();

  // Check authentication first
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get all user roles (check both 'role' and 'roles' for compatibility)
  const userRoles: string[] = [];
  if (user.role) {
    userRoles.push(user.role);
  }
  if (user.roles && Array.isArray(user.roles)) {
    user.roles.forEach(r => {
      if (!userRoles.includes(r)) {
        userRoles.push(r);
      }
    });
  }

  // Check admin requirement
  if (requireAdmin && !userRoles.includes('admin')) {
    return <AccessDenied reason="admin" />;
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    if (!hasRequiredRole) {
      return <AccessDenied reason="role" />;
    }
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const userPermissions = user.permissions || [];
    const hasAllPermissions = requiredPermissions.every(permission =>
      userPermissions.includes(permission)
    );

    if (!hasAllPermissions) {
      return <AccessDenied reason="permission" />;
    }
  }

  // All checks passed, render the child routes
  return <Outlet />;
};

export default PermissionRoute;
